<script setup lang="ts">
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Button from "primevue/button";
import Card from "primevue/card";
import Divider from "primevue/divider";
import Paginator from "primevue/paginator";
import Skeleton from "primevue/skeleton";
import Tag from "primevue/tag";
import { computed, ref } from "vue";
import { SESSIONS_PAGE_LIMIT } from "../constants";
import SessionFields from "../SessionFields/index.vue";
import type { AdvisingSession } from "../types";
import ViewDetails from "../ViewDetails/index.vue";
import {
  accordionTabPt,
  collapseButtonPt,
  dividerPt,
  outerCardPt,
  paginatorPt,
  previousSessionsCardPt,
  sessionCardPt,
  viewDetailButtonPt,
  viewLinkButtonPt,
  warningTagPt,
} from "./styles";
import type { AdvisingSessionsEmits, AdvisingSessionsProps } from "./types";
import { sessionTitleSchema } from "./types";

// ── Props & Emits ──────────────────────────────────────────────────────────

const props = withDefaults(defineProps<AdvisingSessionsProps>(), {
  loading: false,
  error: false,
  previousFetching: false,
  mostRecentSession: undefined,
  offset: 0,
  limit: SESSIONS_PAGE_LIMIT,
  total: 0,
  sessions: () => [],
  canEditTitle: false,
});

const emit = defineEmits<AdvisingSessionsEmits>();

// ── State ──────────────────────────────────────────────────────────────────

const collapsed = ref<boolean>(false);
const expandedIdx = ref<number | undefined>(undefined);
const prevActiveIndex = ref<number | undefined>(undefined);
const detailVisible = ref<boolean>(false);
const selectedSession = ref<AdvisingSession | undefined>(undefined);
const editingSessionId = ref<string | undefined>(undefined);
const editTitle = ref<string>("");
const titleErrorMessage = ref<string>("");

// ── Computed ───────────────────────────────────────────────────────────────

const mostRecentSession = computed<AdvisingSession | undefined>(() => props.mostRecentSession);
const previousSessions = computed<AdvisingSession[]>(() => props.sessions ?? []);
const totalRecords = computed<number>(() => props.total ?? 0);

// ── Methods ────────────────────────────────────────────────────────────────

const toggleExpand = (idx: number): void => {
  expandedIdx.value = expandedIdx.value === idx ? undefined : idx;
};

const onViewDetail = (session: AdvisingSession): void => {
  selectedSession.value = session;
  detailVisible.value = true;
  emit("view-detail", session);
};

const onPageChange = ($event) => {
  expandedIdx.value = undefined;
  emit("page-change", $event.first);
};

const startEdit = (session: AdvisingSession): void => {
  editingSessionId.value = session.id;
  editTitle.value = session.title;
  titleErrorMessage.value = "";
};

const commitEdit = (session: AdvisingSession): void => {
  if (editingSessionId.value === session.id) {
    const result = sessionTitleSchema.safeParse(editTitle.value.trim());
    if (!result.success) {
      titleErrorMessage.value = result.error.issues[0].message;
    } else {
      editingSessionId.value = undefined;
      titleErrorMessage.value = "";
      emit("update-title", session.id, result.data);
    }
  }
};

const cancelEdit = (): void => {
  editingSessionId.value = undefined;
  titleErrorMessage.value = "";
};
</script>

<template>
  <Card :pt="outerCardPt" class="w-full overflow-hidden">

    <!-- ── Card Header slot ── -->
    <template #header>
      <div class="flex items-center justify-between px-4 py-3 bg-white">
        <span class="font-semibold text-sm text-slate-900">
          Advising Sessions ({{ totalRecords + (mostRecentSession ? 1 : 0) }})
        </span>
        <Button :pt="collapseButtonPt" class="p-button-text" @click="collapsed = !collapsed">
          <span
            class="material-symbols-outlined text-2xl leading-none text-blue-darker transition-transform duration-150"
            :class="{ 'rotate-180': !collapsed }"
          >expand_circle_down</span>
        </Button>
      </div>
    </template>

    <!-- ── Card Content slot ── -->
    <template #content>
      <div v-show="!collapsed">

        <!-- Loading state -->
        <template v-if="props.loading && !mostRecentSession">
          <!-- Most recent skeleton -->
          <div class="p-4 space-y-2">
            <Skeleton width="10rem" height="0.75rem"/>
            <Skeleton height="8rem" border-radius="0.5rem"/>
          </div>
          <!-- Previous sessions skeleton -->
          <div class="px-4 pb-4 space-y-2">
            <Skeleton height="3rem" border-radius="0.5rem"/>
          </div>
        </template>

        <!-- Error state -->
        <div v-else-if="props.error && !mostRecentSession" class="px-4 py-5 flex items-start gap-3">
          <span class="material-symbols-outlined text-red-500 text-xl leading-none mt-0.5">error</span>
          <div>
            <p class="text-sm font-semibold text-gray-800">Failed to load advising sessions</p>
            <p class="text-sm text-gray-600 mt-1">Something went wrong. Please try again later.</p>
          </div>
        </div>

        <!-- Empty state -->
        <div v-else-if="!mostRecentSession" class="px-4 py-5">
          <p class="text-sm font-semibold text-gray-800">No advising session yet</p>
          <p class="text-sm text-gray-700 mt-1">This student hasn't had any virtual advising sessions (yet).</p>
        </div>

        <!-- Sessions content -->
        <template v-else>

          <!-- Most Recent Session (pinned) -->
          <div class="p-4">
            <div class="flex items-center gap-1.5 text-xs font-bold tracking-wider text-blue-900 mb-2">
              <span class="w-1.5 h-3 bg-blue-900 rounded-sm inline-block"/>
              MOST RECENT SESSION
            </div>

            <Card :pt="sessionCardPt" class="overflow-hidden">
              <template #header>
                <div class="flex items-center justify-between px-3.5 py-2.5">
                  <div
                    class="flex items-center gap-1.5 bg-gray-100 border border-gray-300 rounded px-2 py-1 text-xs text-gray-800">
                    <span class="material-symbols-outlined text-sm text-gray-700 leading-none">calendar_today</span>
                    <div v-if="mostRecentSession && editingSessionId === mostRecentSession.id" class="flex flex-col">
                      <input
                        :ref="(el) => el && (el as HTMLInputElement).focus()"
                        v-model="editTitle"
                        class="border rounded px-1 py-0.5 text-xs bg-white focus:outline-none focus:ring-1"
                        :class="titleErrorMessage ? 'border-red-500 ring-red-500' : 'border-gray-300 focus:ring-teal-500'"
                        @blur="mostRecentSession && commitEdit(mostRecentSession)"
                        @keydown.enter="mostRecentSession && commitEdit(mostRecentSession)"
                        @keydown.escape="cancelEdit"
                        @click.stop
                      />
                      <span v-if="titleErrorMessage" class="text-xs text-red-500 mt-0.5">{{ titleErrorMessage }}</span>
                    </div>
                    <span
                      v-else-if="props.canEditTitle"
                      class="cursor-pointer hover:underline"
                      @click.stop="mostRecentSession && startEdit(mostRecentSession)"
                    >{{ mostRecentSession?.title }}</span>
                    <span v-else>{{ mostRecentSession?.title }}</span>
                  </div>
                  <Button :pt="viewDetailButtonPt" class="p-button-outlined"
                          @click="mostRecentSession && onViewDetail(mostRecentSession)">
                    <div class="flex items-center">
                      <span class="material-symbols-outlined text-sm mr-1 leading-5">open_in_new</span>
                      <div>View Detail</div>
                    </div>
                  </Button>
                </div>
              </template>

              <template #content>
                <SessionFields
                  :topics="mostRecentSession?.topics"
                  :summary="mostRecentSession?.summary"
                  :rating="mostRecentSession?.rating"
                  :feedback="mostRecentSession?.feedback"
                  ratingLabel="USER STAR RATING"
                />
              </template>
            </Card>
          </div>

          <!-- Previous Sessions Accordion -->
          <div class="px-4 pb-4">
            <Card :pt="previousSessionsCardPt" class="overflow-hidden">
              <template #content>
                <div class="relative">
                  <div v-if="props.previousFetching"
                       class="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-lg">
                    <span class="material-symbols-outlined text-2xl text-teal-500 spin">progress_activity</span>
                  </div>
                  <Accordion :activeIndex="prevActiveIndex">
                    <AccordionTab :pt="accordionTabPt">
                      <template #header>
                        <span class="font-semibold text-sm">
                          Previous Sessions ({{ previousSessions.length }})
                        </span>
                      </template>

                      <div
                        v-for="(session, idx) in previousSessions"
                        :key="session.id"
                        class="transition-colors duration-150 border-l-transparent border-gray-300"
                      >
                        <!-- Row header (always visible) -->
                        <div
                          class="flex items-center justify-between px-3 py-2.5 bg-white cursor-pointer hover:bg-gray-50 transition-colors duration-100"
                          @click="toggleExpand(idx)"
                        >
                          <div class="flex items-center gap-2 flex-1 min-w-0">
                            <span
                              class="material-symbols-outlined text-2xl leading-none text-blue-darker flex-shrink-0 transition-transform duration-150"
                              :class="{ 'rotate-180': expandedIdx === idx }"
                            >expand_circle_down</span>
                            <div v-if="editingSessionId === session.id" class="flex flex-col">
                              <input
                                :ref="(el) => el && (el as HTMLInputElement).focus()"
                                v-model="editTitle"
                                class="border rounded px-1 py-0.5 text-sm font-medium bg-white focus:outline-none focus:ring-1"
                                :class="titleErrorMessage ? 'border-red-500 ring-red-500' : 'border-gray-300 focus:ring-teal-500'"
                                @blur="commitEdit(session)"
                                @keydown.enter="commitEdit(session)"
                                @keydown.escape="cancelEdit"
                                @click.stop
                              />
                              <span v-if="titleErrorMessage" class="text-xs text-red-500 mt-0.5">{{ titleErrorMessage }}</span>
                            </div>
                            <span
                              v-else-if="props.canEditTitle"
                              class="text-sm text-gray-900 font-medium cursor-pointer hover:underline"
                              @click.stop="startEdit(session)"
                            >{{ session.title }}</span>
                            <span
                              v-else
                              class="text-sm text-gray-900 font-medium"
                            >{{ session.title }}</span>
                            <Tag
                              v-if="session.warning"
                              :value="session.warning"
                              :pt="warningTagPt"
                            />
                          </div>
                          <span class="text-xs text-gray-600 flex-shrink-0 ml-3">{{ session.date }}</span>
                        </div>

                        <!-- Expanded detail -->
                        <Transition name="slide">
                          <div v-if="expandedIdx === idx" class="px-4 pt-3 pb-3.5 bg-gray-50">
                            <SessionFields
                              :topics="session.topics"
                              :summary="session.summary"
                              :rating="session.rating"
                              :feedback="session.feedback"
                              ratingLabel="RATING"
                            />
                            <div class="text-right mt-1">
                              <Button :pt="viewLinkButtonPt" class="p-button-link" @click="onViewDetail(session)">
                                <div class="flex items-center">
                                  <div>View Detail</div>
                                  <span class="material-symbols-outlined text-sm ml-1 leading-5">open_in_new</span>
                                </div>
                              </Button>
                            </div>
                          </div>
                        </Transition>

                        <Divider v-if="idx < previousSessions.length - 1" :pt="dividerPt"/>
                      </div>

                      <!-- Pagination -->
                      <Paginator
                        :first="props.offset"
                        :rows="props.limit"
                        :totalRecords="totalRecords"
                        :pt="paginatorPt"
                        @page="onPageChange"
                      />
                    </AccordionTab>
                  </Accordion>
                </div>
              </template>
            </Card>
          </div>

        </template>
      </div>
    </template>

  </Card>

  <ViewDetails
    v-model:visible="detailVisible"
    :title="selectedSession?.title ?? ''"
    :userName="selectedSession?.userName ?? ''"
    :interactions="selectedSession?.chatInteractions ?? []"
  />
</template>

<style scoped>
/* Pagination loading spinner */
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spin {
  display: inline-block;
  animation: spin 0.8s linear infinite;
}

/* Slide transition — cannot be expressed via pt or Tailwind */
.slide-enter-active,
.slide-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  overflow: hidden;
  max-height: 400px;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
